import React from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { useAdmin } from '../../hooks/useAdmin';

/**
 * AdminEditButton - A floating edit button visible only to admins
 * 
 * Appears in the top-right corner of relationship tool pages
 * Links to the Tool Editor for quick content management
 * 
 * @param {string} toolType - Optional: pre-filter to specific tool type in editor
 * @param {string} tab - Optional: which tab to open in the editor (e.g., 'pages')
 * @param {string} className - Additional CSS classes
 */
export const AdminEditButton = ({ toolType, tab, className = '' }) => {
    const { isAdmin, loading } = useAdmin();

    // Don't render anything while loading or if not admin
    if (loading || !isAdmin) {
        return null;
    }

    let editorPath = '/admin/tools';
    if (tab) {
        editorPath = `/admin/tools?tab=${tab}`;
    } else if (toolType) {
        editorPath = `/admin/tools?type=${toolType}`;
    }

    return (
        <Link
            to={editorPath}
            className={`
                fixed top-20 right-4 z-40
                flex items-center gap-2
                px-3 py-2 rounded-full
                bg-[#FFBB98]/90 backdrop-blur-sm
                border border-[#7D8E95]
                text-[#7D8E95] text-sm font-medium
                hover:bg-[#FFBB98] hover:shadow-md
                hover:scale-105
                transition-all duration-200
                ${className}
            `}
            data-testid="admin-edit-content-btn"
        >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Edit Content</span>
        </Link>
    );
};

/**
 * AdminEditInline - An inline edit button for specific sections
 * 
 * Can be placed next to section headers for contextual editing
 */
export const AdminEditInline = ({ label = 'Edit', toolType, className = '' }) => {
    const { isAdmin, loading } = useAdmin();

    if (loading || !isAdmin) {
        return null;
    }

    const editorPath = toolType 
        ? `/admin/tools?type=${toolType}` 
        : '/admin/tools';

    return (
        <Link
            to={editorPath}
            className={`
                inline-flex items-center gap-1.5
                px-2.5 py-1 rounded-full
                bg-[#FFBB98]/50
                text-[#7D8E95] text-xs font-medium
                hover:bg-[#FFBB98] hover:text-[#7D8E95]
                transition-colors duration-200
                ${className}
            `}
            data-testid="admin-edit-inline-btn"
        >
            <Settings className="w-3 h-3" />
            {label}
        </Link>
    );
};

export default AdminEditButton;
